import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {createRefetchContainer, graphql} from 'react-relay';
import {injectIntl} from 'react-intl';
import Moment from 'moment';
import {withRouter} from 'react-router-dom';
import HeaderBar, {buildThirdLevelNavHeader} from '../../shared/components/headers/header-bar/header_bar';
import {
	BUTTON_COLOR,
	BUTTON_STYLE,
	DATE_FORMAT_DAY,
	ELEMENT_TYPE,
	GLOBAL_FILTER_FIELD,
	GLOBAL_FILTER_OPERATOR,
} from '../../../constants';
import {getHolidaysOfTheDay, handlePersonSelected, workingHourForTheDay} from './timesheets_person_data';
import CustomScrollDiv from '../../shared/components/scroll-bars/custom_scroll_div';
import TimesheetsTopSection from './top-section/timesheets_top_section';
import TimesheetsBottomSection from './bottom-section/timesheets_bottom_section';
import {HeaderPageContainer, MyTimesheetWrapper, PageWrapper} from './timesheets_styled';
import {
	handleAdjacentPeriodClick,
	handleDateRangeClick,
	handleTodayButtonClick,
	TIME_PERIOD,
} from './timesheets_change_date_view';
import {withSocketHandling} from '../../../socket/withSocketHandling';
import {getSocketConfig} from './timesheet_socket_handling';
import useEventListener from '../../shared/hooks/useEventListener';
import * as tracking from '../../../tracking';
import Util from '../../shared/util/util';
import {hasFeatureFlag, usingTimeApproval} from '../../shared/util/FeatureUtil';
import {getCompanyLockedDate, getLockedDate} from '../../../components/timesheets/time-lock/TimeLockUtil';
import {TopHeaderBar, TopHeaderBarWrapper} from '../../shared/components/headers/top-header-bar/TopHeaderBar';
import {hasPermission} from '../../shared/util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../../Permissions';
import {MODAL_TYPE, showModal} from '../../shared/components/modals/generic_modal_conductor';

const pageSize = 15;

export const taskTypeDefaultOptions = {
	suggested: {
		id: 'suggested-switch',
		checked: false,
		messageId: 'task_modal.suggested',
		infoMessageId: 'timesheets.suggested_tasks.hover_explanation',
		icon: 'ai-icon',
		nothingReturnedMessageId: 'switch_group.nothing_returned_message.suggested',
		cy: '-suggested',
	},
	starred: {
		id: 'starred-switch',
		checked: false,
		messageId: 'my_tasks.starred',
		val: true,
		infoMessageId: 'timesheets.starred_tasks.hover_explanation',
		nothingReturnedMessageId: 'switch_group.nothing_returned_message.starred',
		cy: '-starred',
	},
	recent: {
		id: 'recent-switch',
		checked: false,
		messageId: 'timesheets.recent',
		infoMessageId: 'timesheets.recent_time_registration.hover_explanation',
		nothingReturnedMessageId: 'switch_group.nothing_returned_message.recent',
		cy: '-recent',
	},
	planned: {
		id: 'planned-switch',
		checked: false,
		messageId: 'expenses.planned',
		infoMessageId: 'timesheets.planned.hover_explanation',
		nothingReturnedMessageId: 'switch_group.nothing_returned_message.planned',
		cy: '-planned',
	},
	inProgress: {
		id: 'inprogress-switch',
		checked: false,
		messageId: 'common.in_progress',
		val: 'INPROGRESS',
		infoMessageId: 'timesheets.in_progress.hover_explanation',
		nothingReturnedMessageId: 'switch_group.nothing_returned_message.in_progress',
		cy: '-inprogress',
	},
};

const timesheetsPage = ({intl, viewer, relay, history, children, setSocketConfig, setSocketVariables}) => {
	if (!viewer.company) return <div></div>;
	if (!Util.AuthorizeViewerAccess('timesheets')) {
		const path = history.location.pathname;
		if (Util.pathIncludesTask(path)) {
			const cardPath = path.substring(Util.indexOfTask(path), path.length);
			history.push('/' + cardPath);
		} else {
			// if user doesnt have access rights to view this page redirect to no access page
			history.push('/not-authorized');
		}
		return <div></div>;
	}
	const {formatMessage} = intl;
	const showPersonDropDown = hasPermission(PERMISSION_TYPE.TIME_REGISTRATION_CREATE_ALL);

	const [showCalendar, setShowCalendar] = useState(
		localStorage.getItem('timesheet-show-calendar') ? JSON.parse(localStorage.getItem('timesheet-show-calendar')) : true
	);
	const [currentDayOfWeek, setCurrentDayOfWeek] = useState(Moment().weekday());
	const [currentViewingDate, setCurrentViewingDate] = useState(Moment());
	const [currentViewingMonth, setCurrentViewingMonth] = useState(Moment());
	const [selectedPersonId, setSelectedPersonId] = useState(viewer.actualPersonId);
	const [selectedSwitch, setSelectedSwitch] = useState(localStorage.getItem('timesheets-selected-switch') || 'suggested');
	const [timePeriod, setTimePeriod] = useState(localStorage.getItem('timesheet-time-period') || TIME_PERIOD.WEEK_VIEW);
	const [weekArray, setWeekArray] = useState([]);
	const [isLoading, setLoading] = useState(false);
	const [googleTask, setGoogleTask] = useState(viewer.task);

	const [showTimeEntryPortal, setShowTimeEntryPortal] = useState(false);
	const [hideModal, setHideModal] = useState(window.innerWidth < 1240);

	const lockedDate = getLockedDate(viewer.company, viewer.company.person);
	const companyLockedDate = useMemo(() => getCompanyLockedDate(viewer.company), []);

	const handleResize = useCallback(() => {
		setHideModal(window.innerWidth < 1240);
		setShowTimeEntryPortal(false);
	});

	useEventListener('resize', handleResize);

	const showTeamTime = Util.AuthorizeViewerAccess('team-time');

	const dateChangeCallback = () => {
		setLoading(false);
	};

	useEffect(() => {
		document.title = 'Timesheets - Forecast';
		const taskIdFromUrl = Util.getUrlQueryParameter('showTaskId');
		if (taskIdFromUrl) {
			relay.refetch({googleTaskId: taskIdFromUrl});
		}
		tracking.trackPage('Timesheet-page', null, null, taskIdFromUrl ? {calendarIntegration: true} : null);

		const selectedPersonIdInt = parseInt(atob(selectedPersonId).replace('Person:', ''));
		const actualPersonIdInt = parseInt(atob(viewer.actualPersonId).replace('Person:', ''));
		setSocketConfig(getSocketConfig([actualPersonIdInt, selectedPersonIdInt]), relay.refetch);
	}, []);

	useEffect(() => {
		const selectedPersonIdInt = parseInt(atob(selectedPersonId).replace('Person:', ''));
		setSocketConfig(getSocketConfig(selectedPersonIdInt));
		// When the person is changed, the switch should be null UNLESS the person is changed back to the actual person
		setSelectedSwitch(
			viewer.actualPersonId === selectedPersonId
				? localStorage.getItem('timesheets-selected-switch') || 'suggested'
				: null
		);
	}, [selectedPersonId]);

	useEffect(() => {
		setGoogleTask(viewer.task);
	}, [viewer.task]);

	const refetch = (variables, renderVariables, callback, options) => {
		relay.refetch(variables, renderVariables, callback, options);
		setSocketVariables(variables);
	};

	const handlePersonChange = person => {
		tracking.trackPageAction('Person Changed');
		setSelectedPersonId(handlePersonSelected(person, currentViewingDate, refetch));
	};

	const toggleCalendar = () => {
		Util.localStorageSetItem('timesheet-show-calendar', !showCalendar);
		setShowCalendar(!showCalendar);
		tracking.trackElementClicked('Toggle-Calendar', {state: showCalendar ? 'hide' : 'show'});
		setShowTimeEntryPortal(false);
	};

	//Today Button
	const goToToday = () => {
		setLoading(true);
		setCurrentViewingDate(handleTodayButtonClick(selectedPersonId, refetch, dateChangeCallback));
		setCurrentViewingMonth(Moment());
	};

	//Next/Previous date range buttons
	const adjacentPeriod = (calendareShown, forward) => {
		//if the day selected is a day outside of the month selected we don't want to jump to the next/previous month but to the month oof the day selected
		const keepMonth = calendareShown && currentViewingDate.month() !== currentViewingMonth.month() ? 0 : 1;
		setLoading(true);
		const newDate = handleAdjacentPeriodClick(
			currentViewingDate,
			forward,
			calendareShown ? TIME_PERIOD.MONTH_VIEW : timePeriod,
			selectedPersonId,
			refetch,
			dateChangeCallback,
			keepMonth
		);

		setCurrentViewingDate(newDate);
		setCurrentViewingMonth(newDate);
	};

	//Date picker
	const handleDateRange = (calendareShown, date, passedPersonId) => {
		setLoading(true);
		const newDate = handleDateRangeClick(
			date,
			calendareShown ? TIME_PERIOD.MONTH_VIEW : timePeriod,
			passedPersonId || selectedPersonId,
			refetch,
			dateChangeCallback
		);
		setCurrentViewingDate(newDate);
		setCurrentViewingMonth(newDate);
	};

	useEffect(() => {
		if (showPersonDropDown) {
			const dateFromURL = Util.getUrlQueryParameter('date');
			if (dateFromURL) {
				const splitDate = dateFromURL.split('-');
				if (splitDate.length === 3) {
					const date = dateFromURL ? Util.CreateNonUtcMomentDate(splitDate[0], splitDate[1], splitDate[2]) : null;
					const personIdFromURL = Util.getUrlQueryParameter('personId');
					if (date && personIdFromURL) {
						setSelectedPersonId(personIdFromURL);
						setTimePeriod(TIME_PERIOD.WEEK_VIEW);
						handleDateRange(true, date, personIdFromURL);
					}
				}
			}
		}
	}, []);

	//Timesheet Month calendar (not the date picker)
	const selectDayFromCalendar = useCallback(
		(day, isWeek) => {
			//TODO improve this to reduce the number of setState
			setTimePeriod(isWeek ? TIME_PERIOD.WEEK_VIEW : TIME_PERIOD.DAY_VIEW);
			const todayIsInWeek = day.clone().week() === Moment().week();

			// If week selected, set CurrentViewingDate to today if today is in the selected week, or the same weekday as what was selected before if not.
			setCurrentViewingDate(
				isWeek
					? todayIsInWeek
						? Moment()
						: day
								.clone()
								//.startOf('week')
								.weekday(currentDayOfWeek)
					: day
			);
		},
		[currentViewingMonth, currentDayOfWeek]
	);

	//Switch between Day and Week
	const setTimePeriodWithDate = period => {
		setTimePeriod(period);
	};

	const buildWeekArr = (currentDate, limit) => {
		const weekArray = [];
		const rec = (date, acc, limit) => {
			if (limit > 0) {
				acc.push(date);
				rec(date.clone().add(1, 'days'), acc, limit - 1);
			}
			return acc;
		};
		return rec(currentDate.clone().startOf('week'), weekArray, limit);
	};

	useEffect(() => {
		Util.localStorageSetItem('timesheet-time-period', timePeriod);
	}, [timePeriod]);

	useEffect(() => {
		if (timePeriod === TIME_PERIOD.DAY_VIEW) {
			setCurrentDayOfWeek(currentViewingDate.weekday());
		}

		// In order to avoid building weekArray on every render, it is stored in state and only rebuilt when currentViewingDate or timePeriod changes.
		const tempWeekArray = timePeriod === TIME_PERIOD.WEEK_VIEW ? buildWeekArr(currentViewingDate, 7) : [];
		if (tempWeekArray !== weekArray) {
			setWeekArray(tempWeekArray);
		}
	}, [currentViewingDate, timePeriod]);

	const useThirdLevelNav = viewer.availableFeatureFlags.some(flag => flag.key === 'third_level_nav');

	const getHeaderTitle = () => {
		const content = [];
		const onboardingFlows = [
			{
				id: 'timesheets-introduction',
				title: 'Timesheets Intro',
				description: null,
				contentId: '1595417577bGaw4125',
			},
			{id: 'timesheets-registering-faster', title: 'Timesheets - Register time faster', contentId: '1595427501vIrn1383'},
			{
				id: 'timesheets-registering-idletime',
				title: 'Timesheets - Register Meetings or Vacation',
				description: null,
				contentId: '1595427489uBpc4704',
			},
		];

		if (showTeamTime) {
			onboardingFlows.push({
				id: 'timesheets-team-time',
				title: "Seeing your Team's Time registrations",
				contentId: '1595427503vJkr389',
			});
		}

		const onboardingComponent = {
			id: 'onboarding-component',
			intl: intl,
			type: TopHeaderBar.TYPE.ONBOARDING,
			title: formatMessage({id: 'timesheets.onboarding_title'}),
			options: onboardingFlows,
		};
		content.push(onboardingComponent);
		return (
			<TopHeaderBarWrapper className="top-header-bar">
				<TopHeaderBar title={intl.formatMessage({id: 'timesheet.my_timesheet'})} content={content} />
			</TopHeaderBarWrapper>
		);
	};

	// The CVS export date - taken from the current selected month
	const csvStartDate = Moment([currentViewingMonth.year(), currentViewingMonth.month()]);
	const csvEndDate = currentViewingMonth.clone().endOf('month');

	// The CVS search query values
	const csvSearchQuery = {
		filters: [
			{
				field: GLOBAL_FILTER_FIELD.WITH_TIME_REG,
				operator: GLOBAL_FILTER_OPERATOR.IS,
				value: true.toString(),
			},
			{
				field: GLOBAL_FILTER_FIELD.START_DATE_TIME_REG,
				operator: GLOBAL_FILTER_OPERATOR.GREATER_OR_EQUAL,
				value: csvStartDate.format(DATE_FORMAT_DAY),
			},
			{
				field: GLOBAL_FILTER_FIELD.END_DATE_TIME_REG,
				operator: GLOBAL_FILTER_OPERATOR.LESS_OR_EQUAL,
				value: csvEndDate.format(DATE_FORMAT_DAY),
			},
			{
				field: GLOBAL_FILTER_FIELD.PERSON_TIME_REG,
				operator: GLOBAL_FILTER_OPERATOR.IS,
				value: [selectedPersonId],
			},
		],
	};

	const handleCsvDownload = () => {
		showModal({
			type: MODAL_TYPE.REPORTED_TIME_DATE_CSV,
			searchQuery: csvSearchQuery,
			enabledColumns: {
				project: true,
				date: true,
				reportedTime: true,
				task: true,
				notes: true,
			},
		});
	};

	const getHeader = () => {
		const leftContent = [];
		const rightContent = [];
		if (showPersonDropDown) {
			const personDropdown = {
				type: ELEMENT_TYPE.DROPDOWN_PERSON,
				customWidth: 172,
				selectedPerson: selectedPersonId,
				callback: handlePersonChange,
				userpilot: 'timesheets-select-person',
			};
			leftContent.push(personDropdown);
		}

		const dateChanger = {
			type: ELEMENT_TYPE.DATE_CHANGER_V2,
			useTodayButton: true,
			timePeriod: showCalendar ? TIME_PERIOD.MONTH_VIEW : timePeriod,
			//
			id: 'time-date-select',
			currentViewingDate: showCalendar ? currentViewingMonth : currentViewingDate,
			handleTodayButtonClick: goToToday,
			handleAdjacentDateButtonClick: adjacentPeriod.bind(this, showCalendar),
			updateDateRange: handleDateRange.bind(this, showCalendar),
			boldLongDayMonth: true,
			selectorTooltipEnabled: true,
			selectorTooltipProps: {
				autoPlace: true,
				grey: true,
				infoText: formatMessage({id: 'common.select_date'}),
			},
			tooltipEnabled: true,
			tooltipProps: {
				autoPlace: true,
				infoText: Moment().format('ddd, DD. MMM') + ' ' + Moment().format('YYYY'),
				grey: true,
			},
			userpilot: 'timesheets-select-month',
		};

		leftContent.push(dateChanger);

		const periodToggle = {
			type: ELEMENT_TYPE.UNIT_TOGGLE,
			options: [
				{
					id: 'day',
					text: formatMessage({id: 'overview_time.day'}),
					onClick: () => {
						setTimePeriodWithDate(TIME_PERIOD.DAY_VIEW);
						tracking.trackElementClicked(TIME_PERIOD.DAY_VIEW + '-view');
					},
					selected: timePeriod === TIME_PERIOD.DAY_VIEW,
					dataCy: 'day',
				},
				{
					id: 'week',
					text: formatMessage({id: 'overview_time.week'}),
					onClick: () => {
						setTimePeriodWithDate(TIME_PERIOD.WEEK_VIEW);
						tracking.trackElementClicked(TIME_PERIOD.WEEK_VIEW + '-view');
					},
					selected: timePeriod === TIME_PERIOD.WEEK_VIEW,
					dataCy: 'week',
				},
			],
		};

		leftContent.push(periodToggle);

		if (!showCalendar || hideModal) {
			const addTime = {
				id: 'add-time-entry-button',
				type: ELEMENT_TYPE.BUTTON,
				text: formatMessage({id: 'common.new_time_entry_popup_title'}),
				callback: () => {
					setShowTimeEntryPortal(!showTimeEntryPortal);
					tracking.trackElementClicked((!showTimeEntryPortal ? 'Show' : 'Hide') + '-Log-Time-Section');
				},
				style: showTimeEntryPortal ? BUTTON_STYLE.FILLED : BUTTON_STYLE.OUTLINE,
				color: BUTTON_COLOR.PURPLE,
				cy: 'new-ui-scoping-custom-button-container',
				dataCy: 'overview-time-add-time-reg',
			};
			rightContent.push(addTime);
		}

		if (!useThirdLevelNav) {
			const onboardingFlows = [
				{
					id: 'timesheets-introduction',
					title: 'Timesheets Intro',
					description: null,
					contentId: '1595417577bGaw4125',
				},
				{
					id: 'timesheets-registering-faster',
					title: 'Timesheets - Register time faster',
					contentId: '1595427501vIrn1383',
				},
				{
					id: 'timesheets-registering-idletime',
					title: 'Timesheets - Register Meetings or Vacation',
					description: null,
					contentId: '1595427489uBpc4704',
				},
			];

			if (showTeamTime) {
				onboardingFlows.push({
					id: 'timesheets-team-time',
					title: "Seeing your Team's Time registrations",
					contentId: '1595427503vJkr389',
				});
			}

			const onboardingComponent = {
				id: 'onboarding-component',
				intl: intl,
				type: ELEMENT_TYPE.ONBOARDING_POPUP,
				title: formatMessage({id: 'timesheets.onboarding_title'}),
				options: onboardingFlows,
			};
			rightContent.push(onboardingComponent);
		}

		// CSV Download
		if (hasFeatureFlag('my_timesheet_csv', viewer.availableFeatureFlags)) {
			rightContent.push({
				type: ELEMENT_TYPE.CSV,
				callback: handleCsvDownload,
			});
		}

		const hideShow = {
			type: ELEMENT_TYPE.HIDE_AND_SHOW,
			cy: 'calendar-toggle',
			userpilot: 'timesheets-toggle-calender',
			showId: 'show-calendar',
			hideId: 'hide-calendar',
			hideText: formatMessage({id: 'timesheet.hide_calendar'}),
			showText: formatMessage({id: 'timesheet.show_calendar'}),
			isShowing: !showCalendar,
			callback: toggleCalendar,
		};
		rightContent.push(hideShow);

		if (useThirdLevelNav) {
			return buildThirdLevelNavHeader(leftContent, rightContent);
		}

		return <HeaderBar leftContent={leftContent} rightContent={rightContent} />;
	};

	const currentPersonViewed = viewer.company.person;

	const currentViewingDateMemo = useMemo(() => currentViewingDate.clone(), [currentViewingDate]);

	const lastWorkingDayInWeek = useMemo(() => {
		if (timePeriod !== TIME_PERIOD.WEEK_VIEW || weekArray.length === 0) return currentViewingDate;
		// Iterate through array backwards. This way, the first working day found will be the last working day of the week.
		return weekArray
			.slice()
			.reverse()
			.find(day => {
				const workingHours = workingHourForTheDay(currentPersonViewed, day);
				const holidaysForDay = getHolidaysOfTheDay(currentPersonViewed, day);
				// A working day has more than 0 workingHours and exactly 0 holidays
				return workingHours !== 0 && holidaysForDay.length === 0;
			});
	}, [weekArray, currentPersonViewed]);

	return (
		<PageWrapper data-cy={'timesheets-page'}>
			{children}
			{useThirdLevelNav ? <HeaderPageContainer>{getHeaderTitle()}</HeaderPageContainer> : null}
			<HeaderPageContainer>{getHeader()}</HeaderPageContainer>
			<CustomScrollDiv className="custom-scrollbar-div">
				<MyTimesheetWrapper>
					<TimesheetsTopSection
						viewer={viewer}
						refetch={refetch}
						pageSize={pageSize}
						hideModal={hideModal}
						showCalendar={showCalendar}
						currentViewingDate={currentViewingDateMemo}
						currentViewingMonth={currentViewingMonth}
						weekArray={weekArray}
						timePeriod={timePeriod}
						selectDayFromCalendar={selectDayFromCalendar}
						selectedPerson={currentPersonViewed}
						showTimeEntryPortal={showTimeEntryPortal}
						setShowTimeEntryPortal={setShowTimeEntryPortal}
						calenderLoading={isLoading}
						googleTask={googleTask}
						lockedDate={lockedDate}
						hasOpportunityAccess={Util.hasOpportunityAccess(viewer.company.modules)}
					/>
					<TimesheetsBottomSection
						currentViewingDate={currentViewingDateMemo}
						lastWorkingDayInWeek={lastWorkingDayInWeek}
						timeRegistrations={viewer.timeRegistrations.edges}
						timePeriod={timePeriod}
						groupLocked={viewer.actualPersonId !== selectedPersonId}
						selectedPerson={currentPersonViewed}
						selectedSwitch={selectedSwitch}
						setSelectedSwitch={setSelectedSwitch}
						selectDayFromCalendar={selectDayFromCalendar}
						lockedDate={lockedDate}
						companyLockedDate={companyLockedDate}
						availableFeatureFlags={viewer.availableFeatureFlags}
						usingTimeApproval={usingTimeApproval(viewer.company)}
					/>
				</MyTimesheetWrapper>
			</CustomScrollDiv>
		</PageWrapper>
	);
};

// RELAY SECTION

const timesheetsPageQuery = graphql`
	query timesheetsPage_Query(
		$personId: ID
		$googleTaskId: ID
		$pageSize: Int
		$startDateString: String
		$endDateString: String
	) {
		viewer {
			actualPersonId
			component(name: "timesheet")
			...timesheetsPage_viewer
				@arguments(
					personId: $personId
					googleTaskId: $googleTaskId
					pageSize: $pageSize
					startDateString: $startDateString
					endDateString: $endDateString
				)
		}
	}
`;

export {timesheetsPageQuery};

export default withRouter(
	withSocketHandling(
		injectIntl(
			createRefetchContainer(
				timesheetsPage,
				{
					viewer: graphql`
						fragment timesheetsPage_viewer on Viewer
						@argumentDefinitions(
							personId: {type: "ID"}
							googleTaskId: {type: "ID"}
							pageSize: {type: "Int"}
							startDateString: {type: "String"}
							endDateString: {type: "String"}
						) {
							id
							harvestUser
							unit4User
							actualPersonId
							email
							backendId
							language
							availableFeatureFlags {
								key
							}
							company {
								id
								useTimeApproval
								harvestEnabled
								unit4Enabled
								characterLimit
								lockedPeriodYear
								lockedPeriodMonth
								lockedPeriodDay
								modules {
									moduleType
								}
								idleTimes(first: 100000) {
									edges {
										node {
											id
											name
											isInternalTime
											favoured
											disabled
										}
									}
								}
								person(id: $personId) {
									holidayCalendar {
										id
										holidayCalendarEntries(first: 10000) {
											edges {
												node {
													id
													day
													month
													year
													name
												}
											}
										}
										name
									}
									id
									role {
										id
										name
									}
									firstName
									lastName
									fullName
									createdAt
									startDate
									endDate
									systemUser
									initials
									active
									monday
									tuesday
									wednesday
									thursday
									friday
									saturday
									sunday
									excludeFromCompanyLockedPeriod
									submitLockedDateYear
									submitLockedDateMonth
									submitLockedDateDay
									harvestUser
									unit4User
									economicUser
									profilePictureId
									profilePictureDefaultId
								}
							}
							projects(first: 100000) {
								edges {
									...ProjectDropdown_projects
									node {
										id
										name
										fullAccessToProject
										companyProjectId
										status
										billable
										projectColor
										harvestProject {
											id
										}
										unit4Project {
											id
											activitiesEnabled
										}
										isJiraProject
										timelogProject {
											id
										}
									}
								}
							}
							timeRegistrations(
								first: 100000
								personId: $personId
								startDate: $startDateString
								endDate: $endDateString
								verifyPermissionRights: false
							) @connection(key: "Viewer_timeRegistrations", filters: []) {
								edges {
									node {
										id
										day
										month
										year
										minutesRegistered
										notes
										invoiced
										xeroInvoiceId
										lockedInPeriod
										harvestTimeId
										allocationId
										harvestTaskIdInt
										harvestError
										unit4ActivityId
										approvalStatus
										project {
											id
											name
											status
											projectColor
											companyProjectId
											estimationUnit
											fullAccessToProject
											harvestProject {
												id
												name
											}
											unit4Project {
												id
												name
												activitiesEnabled
											}
											client {
												id
												name
											}
										}
										task {
											id
											name
											progress
											companyTaskId
											highPriority
											approved
											timeLeft
											favoured
											fullAccessToProject
											latestUiUpdateAt
											timeLeft
											parentTaskId
											project {
												id
												name
												status
												companyProjectId
												projectColor
												estimationUnit
												fullAccessToProject
												client {
													id
													name
												}
												harvestProject {
													id
													name
												}
												unit4Project {
													id
													name
													activitiesEnabled
												}
											}
											phase {
												id
												name
											}
										}
										idleTime {
											id
											name
											isInternalTime
											favoured
											disabled
										}
										person {
											id
											firstName
											lastName
											isViewer
										}
									}
								}
							}
							task(id: $googleTaskId) {
								id
								name
								companyTaskId
								timeLeft
								fullAccessToProject
								phase {
									id
									name
								}
								project {
									id
									name
									projectColor
									companyProjectId
									fullAccessToProject
									harvestProject {
										id
									}
									unit4Project {
										id
										activitiesEnabled
									}
									timelogProject {
										id
									}
									client {
										id
										name
									}
								}
								statusColumnV2 {
									id
									name
									category
								}
								assignedPersons {
									id
									fullName
									active
									role {
										id
										name
									}
								}
							}
						}
					`,
				},
				graphql`
					query timesheetsPageRefetchQuery(
						$personId: ID
						$googleTaskId: ID
						$pageSize: Int
						$startDateString: String
						$endDateString: String
					) {
						viewer {
							component(name: "timesheet")
							...timesheetsPage_viewer
								@arguments(
									personId: $personId
									googleTaskId: $googleTaskId
									pageSize: $pageSize
									startDateString: $startDateString
									endDateString: $endDateString
								)
						}
					}
				`
			)
		)
	)
);
